/**
 * Tiny typed fetch wrapper around the worker's HTTP API.
 *
 * Routes are relative — Vite's dev proxy forwards `/quotes`, `/info`,
 * `/dev`, `/health` to wrangler on :8787; in prod the same Worker
 * serves the SPA and the API from one origin so the same relative
 * paths just work.
 */

import { getAuthToken, useAuthStore } from "../auth/authStore";

/**
 * Attach the SIWE session bearer token when one is present. Harmless on
 * public routes (they ignore it); required on `/transactions-bundle/*`.
 */
function authHeaders(base: Record<string, string>): Record<string, string> {
	const token = getAuthToken();
	return token ? { ...base, authorization: `Bearer ${token}` } : base;
}

/** A 401 means the session is gone/expired — drop it so the UI re-prompts. */
function handleUnauthorized(status: number): void {
	if (status === 401) useAuthStore.getState().logout();
}

export async function getJson<T>(path: string): Promise<T> {
	const res = await fetch(path, {
		headers: authHeaders({ accept: "application/json" }),
	});
	if (!res.ok) {
		handleUnauthorized(res.status);
		const body = await res.text().catch(() => "");
		throw new Error(`${path} → ${res.status} ${res.statusText}: ${body}`);
	}
	return res.json() as Promise<T>;
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
	const res = await fetch(path, {
		method: "POST",
		headers: authHeaders({
			"content-type": "application/json",
			accept: "application/json",
		}),
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		handleUnauthorized(res.status);
		const text = await res.text().catch(() => "");
		throw new Error(`${path} → ${res.status} ${res.statusText}: ${text}`);
	}
	return res.json() as Promise<T>;
}

export function describe(v: unknown): string {
	if (Array.isArray(v)) return `array of length ${v.length}`;
	if (v == null) return String(v);
	if (typeof v !== "object") return typeof v;
	return `object with keys [${Object.keys(v as Record<string, unknown>).join(", ")}]`;
}
